// src/pages/CampaignDetail.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getCampaign, getConnections } from '../api';

const statusColors = {
  draft: 'bg-gray-400',
  scheduled: 'bg-yellow-500',
  processing: 'bg-blue-500',
  completed: 'bg-green-500',
  failed: 'bg-red-500',
  paused: 'bg-orange-500',
};

export default function CampaignDetail() {
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [media, setMedia] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Optional: map from_mobile to connection name (for display)
  const [connections, setConnections] = useState([]);

  const fetchCampaign = async (page = 1) => {
    setLoading(true);
    try {
      const res = await getCampaign(id, page, 20);
     // In CampaignDetail.jsx, after fetchCampaign:
    if (res.status === 1) {
      setCampaign(res.data.campaign);
      setMedia(res.data.media || []);
      setRecipients(res.data.recipients || []);
      setPagination(res.data.pagination || { page: 1, limit: 20, total: 0, totalPages: 1 });
    }
    } catch (e) {
      console.error('Failed to fetch campaign', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaign();
    // Fetch connections to show friendly names for from_mobile
    getConnections()
      .then(res => {
        if (res.status === 1) {
          setConnections(res.data || []);
        }
      })
      .catch(console.error);
  }, [id]);

  const goToPage = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      fetchCampaign(page);
    }
  };

  // Get connection name or phone number from a mobile number
  const getSenderLabel = (mobile) => {
    if (!mobile) return '—';
    const conn = connections.find(c => c.phone_number === mobile);
    return conn ? (conn.name || conn.phone_number) : mobile;
  };

  // Filter recipients by search term
  const filteredRecipients = useMemo(() => {
    if (!searchTerm.trim()) return recipients;
    const term = searchTerm.trim().toLowerCase();
    return recipients.filter(
      r =>
        (r.mobile && r.mobile.includes(term)) ||
        (r.lead_name && r.lead_name.toLowerCase().includes(term))
    );
  }, [recipients, searchTerm]);

  if (loading && !campaign) return <div className="text-center py-10 text-gray-500">Loading…</div>;
  if (!campaign) return <div className="text-center py-10">Campaign not found</div>;

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-center gap-4 flex-wrap mb-6">
        <Link to="/campaigns" className="text-blue-600 hover:underline font-medium">← Back</Link>
        <h2 className="text-2xl font-bold text-gray-900">{campaign.name}</h2>
        <span className={`px-3 py-1 rounded-full text-sm font-medium text-white ${statusColors[campaign.status] || 'bg-gray-400'}`}>
          {campaign.status}
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <span className="text-xs text-gray-400 uppercase">Total</span>
          <p className="text-2xl font-bold">{campaign.total || 0}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <span className="text-xs text-gray-400 uppercase">Sent</span>
          <p className="text-2xl font-bold text-green-600">{campaign.sent || 0}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <span className="text-xs text-gray-400 uppercase">Failed</span>
          <p className="text-2xl font-bold text-red-600">{campaign.failed || 0}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <span className="text-xs text-gray-400 uppercase">Pending</span>
          <p className="text-2xl font-bold text-yellow-600">{campaign.pending || 0}</p>
        </div>
      </div>

      {/* Meta */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 mb-6">
        <p><strong>Message:</strong> {campaign.message_text}</p>
        <p><strong>Delay:</strong> {campaign.delay_seconds}s</p>
        {campaign.scheduled_at && <p><strong>Scheduled:</strong> {new Date(campaign.scheduled_at).toLocaleString()}</p>}
      </div>

      {/* Media */}
      {media.length > 0 ? (
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 mb-6">
          <h4 className="font-semibold mb-2">Media Attachments</h4>
          <div className="space-y-1">
            {media.map((m) => (
              <div key={m.id} className="flex items-center gap-3 flex-wrap bg-gray-50 p-2 rounded border border-gray-200">
                <span className="bg-gray-200 px-2 py-0.5 rounded text-xs uppercase font-medium">{m.media_type}</span>
                <span className="font-mono text-sm break-all">{m.media_url}</span>
                {m.caption && <span className="text-gray-500 italic text-sm">“{m.caption}”</span>}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-gray-400 text-sm mb-4">No media attached</div>
      )}

      {/* Recipients */}
      <div>
        <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
          <h4 className="font-semibold">Recipients</h4>
          <input
            type="text"
            placeholder="Search by name or mobile..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-64"
          />
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">#</th>
                <th className="text-left px-4 py-2 font-semibold">Mobile</th>
                <th className="text-left px-4 py-2 font-semibold">Name</th>
                <th className="text-left px-4 py-2 font-semibold">From</th>
                <th className="text-left px-4 py-2 font-semibold">Status</th>
                <th className="text-left px-4 py-2 font-semibold">Sent At</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecipients.length > 0 ? (
                filteredRecipients.map((r, idx) => (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2">{(pagination.page - 1) * pagination.limit + idx + 1}</td>
                    <td className="px-4 py-2">{r.mobile}</td>
                    <td className="px-4 py-2">{r.lead_name || '—'}</td>
                    <td className="px-4 py-2">{getSenderLabel(r.from_mobile)}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium text-white ${statusColors[r.status] || 'bg-gray-400'}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2">{r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="6" className="text-center py-4 text-gray-400">No recipients match your search</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {pagination.totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-4">
            <button onClick={() => goToPage(pagination.page - 1)} disabled={pagination.page === 1} className="px-4 py-1 border rounded disabled:opacity-40">◀ Prev</button>
            <span>Page {pagination.page} of {pagination.totalPages}</span>
            <button onClick={() => goToPage(pagination.page + 1)} disabled={pagination.page === pagination.totalPages} className="px-4 py-1 border rounded disabled:opacity-40">Next ▶</button>
          </div>
        )}
      </div>
    </div>
  );
}